import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { ago, api, Drawer, Empty, Loading, Pill, useLoad, useMe } from "./lib";

const KIND: Record<string, { label: string; tone: string; act: string }> = {
  task: { label: "Task", tone: "info", act: "Make the task" },
  draft: { label: "Claude can draft it", tone: "accent", act: "Write it now" },
  research: { label: "Claude can look into it", tone: "accent", act: "Ask Claude to look into it" },
  decision: { label: "Your call", tone: "warn", act: "Add to Today" },
  idea: { label: "Idea", tone: "", act: "Keep it" },
};
const DRAFT_KEY = "momeaze.braindump.draft";
const load = () => {
  try { return localStorage.getItem(DRAFT_KEY) ?? ""; } catch { return ""; }
};
const save = (v: string) => {
  try { v ? localStorage.setItem(DRAFT_KEY, v) : localStorage.removeItem(DRAFT_KEY); } catch {}
};

// Type anything. Claude sorts it into tasks, drafts, research, decisions and ideas; nothing happens until you say yes.
export function BrainDump() {
  const { toast } = useMe();
  const { data, error, reload } = useLoad<any[]>("/api/braindump");
  const users = (useLoad<any[]>("/api/users").data || []).filter((u) => u.kind !== "agent" && u.active !== 0);
  const [text, setText] = useState(load);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api("/api/braindump", { method: "POST", body: { text } });
      setText("");
      save("");
      if (r.error) toast(r.error, true);
      reload();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page">
      <section className="card stack">
        <strong>Brain dump</strong>
        <span className="muted small">Type everything on your mind, in any order. Claude sorts it into tasks, drafts it can write, things to look into, decisions for you, and ideas to keep. Nothing is created or sent until you say yes.</span>
        <textarea
          value={text}
          onChange={(e) => (setText(e.target.value), save(e.target.value))}
          onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && submit()}
          rows={7}
          className="input"
          placeholder="e.g. Need to follow up with Zomee about December. Paige should post the Ollie piece Thursday. Can Claude find what Tiny Trials said about their code? Should we do Black Friday at $69.99…"
          aria-label="Brain dump"
        />
        <div className="row between">
          <span className="muted tiny">{text ? "Saved on this device as you type." : "⌘ + Return to send."}</span>
          <button className="btn primary" disabled={busy || !text.trim()} onClick={submit}>{busy ? "Sorting…" : "Sort it out"}</button>
        </div>
      </section>
      {!data ? <Loading error={error} /> : data.length ? data.map((d) => <Dump key={d.id} d={d} users={users} onChange={reload} />) : <Empty>Your brain dumps show up here.</Empty>}
    </div>
  );
}

function Dump({ d, users, onChange }: { d: any; users: any[]; onChange: () => void }) {
  const { toast } = useMe();
  const [showText, setShowText] = useState(false);
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      await api(`/api/braindump/${d.id}/retry`, { method: "POST" });
      onChange();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!confirm("Delete this brain dump? Tasks and items already made stay.")) return;
    await api(`/api/braindump/${d.id}`, { method: "DELETE" });
    onChange();
  };
  const open = d.items.filter((i: any) => i.status === "suggested").length;
  return (
    <section className="card stack">
      <div className="row between">
        <div className="stack" style={{ gap: 2 }}>
          <strong style={{ fontSize: 15 }}>{d.summary || (d.error ? "Not sorted yet" : "Sorting…")}</strong>
          <span className="muted tiny">{ago(d.created_at)}{d.items.length ? ` · ${d.items.length} items${open ? `, ${open} waiting on you` : ""}` : ""}</span>
        </div>
        <div className="row">
          <button className="btn ghost sm" onClick={() => setShowText(!showText)}>{showText ? "Hide what I wrote" : "What I wrote"}</button>
          <button className="btn ghost sm" onClick={remove}>Delete</button>
        </div>
      </div>
      {showText && <div className="small" style={{ whiteSpace: "pre-wrap", background: "var(--panel-2, #f6f4f8)", padding: 10, borderRadius: 8 }}>{d.text}</div>}
      {d.error && (
        <div className="banner warn small row between">
          <span>{d.error}</span>
          <button className="btn sm" disabled={busy} onClick={retry}>{busy ? "Sorting…" : "Try again"}</button>
        </div>
      )}
      {d.questions.length > 0 && (
        <div className="small"><strong>Claude wasn't sure: </strong>{d.questions.join(" ")}</div>
      )}
      {d.items.map((it: any, n: number) => <Item key={n} dumpId={d.id} n={n} it={it} users={users} onChange={onChange} />)}
    </section>
  );
}

function Item({ dumpId, n, it, users, onChange }: { dumpId: string; n: number; it: any; users: any[]; onChange: () => void }) {
  const { toast } = useMe();
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ title: it.title, detail: it.detail, kind: it.kind, owner_id: it.owner_id, due_on: it.due_on ?? "", priority: it.priority });
  const [busy, setBusy] = useState(false);
  const k = KIND[edit ? f.kind : it.kind] ?? KIND.idea!;
  const send = async (action: string) => {
    setBusy(true);
    try {
      await api(`/api/braindump/${dumpId}/items/${n}`, { method: "POST", body: action === "do" && edit ? { action, ...f, due_on: f.due_on || null } : { action } });
      setEdit(false);
      onChange();
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const owner = users.find((u) => u.id === it.owner_id)?.name?.split(" ")[0];
  const done = it.status === "done" || it.status === "kept";
  return (
    <div className="stack" style={{ gap: 6, padding: "10px 0", borderTop: "1px solid var(--line, #eee)", opacity: it.status === "skipped" ? 0.55 : 1 }}>
      <div className="row between" style={{ gap: 8, alignItems: "flex-start" }}>
        <div className="stack" style={{ gap: 2, flex: 1 }}>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <Pill tone={k.tone}>{k.label}</Pill>
            <strong style={{ fontSize: 14 }}>{it.title}</strong>
          </div>
          <span className="small muted">{it.detail}</span>
          <span className="tiny muted">
            {[it.kind === "task" && owner ? `For ${owner}` : "", it.due_on ? `Due ${it.due_on}` : "", it.priority !== "normal" ? it.priority : "", it.why].filter(Boolean).join(" · ")}
          </span>
          {it.next_step && it.status === "suggested" && <span className="small"><span style={{ color: "var(--accent-ink)", fontWeight: 600 }}>First step: </span>{it.next_step}</span>}
        </div>
        {it.status === "suggested" && !edit && (
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            <button className="btn primary sm" disabled={busy} onClick={() => send("do")}>{busy ? "Working…" : k.act}</button>
            <button className="btn ghost sm" onClick={() => setEdit(true)}>Change</button>
            <button className="btn ghost sm" onClick={() => send("skip")}>Skip</button>
          </div>
        )}
        {it.status === "skipped" && <button className="btn ghost sm" onClick={() => send("undo_skip")}>Undo skip</button>}
        {done && <Outcome it={it} />}
      </div>

      {edit && (
        <div className="stack" style={{ gap: 6 }}>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} aria-label="Title" />
          <textarea className="input" value={f.detail} rows={2} onChange={(e) => setF({ ...f, detail: e.target.value })} aria-label="Detail" style={{ minHeight: 60 }} />
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <select className="input" style={{ width: "auto" }} value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} aria-label="Kind">
              {Object.entries(KIND).map(([v, x]) => <option key={v} value={v}>{x.label}</option>)}
            </select>
            {f.kind === "task" && (
              <select className="input" style={{ width: "auto" }} value={f.owner_id} onChange={(e) => setF({ ...f, owner_id: e.target.value })} aria-label="Owner">
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            {(f.kind === "task" || f.kind === "decision") && <input className="input" style={{ width: "auto" }} type="date" value={f.due_on} onChange={(e) => setF({ ...f, due_on: e.target.value })} aria-label="Due" />}
            <select className="input" style={{ width: "auto" }} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} aria-label="Priority">
              {["low", "normal", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn primary sm" disabled={busy} onClick={() => send("do")}>{busy ? "Working…" : (KIND[f.kind] ?? KIND.idea!).act}</button>
            <button className="btn ghost sm" onClick={() => setEdit(false)}>Cancel</button>
          </div>
        </div>
      )}

      {it.kind === "draft" && it.result && <DraftResult it={it} onRedo={() => send("do")} busy={busy} />}
      {it.kind === "research" && it.live?.result && (
        <div className="small" style={{ whiteSpace: "pre-wrap", background: "var(--panel-2, #f6f4f8)", padding: 10, borderRadius: 8 }}>
          <strong>What Claude found: </strong>{it.live.result}
          {it.live.result_url && <> <a href={it.live.result_url} target="_blank" rel="noreferrer">Open</a></>}
        </div>
      )}
    </div>
  );
}

function Outcome({ it }: { it: any }) {
  const live = it.live;
  const text =
    it.status === "kept" ? "Kept as an idea"
    : it.kind === "task" ? (live ? `Task made${live.status === "done" ? ", done" : ""}` : "Task made")
    : it.kind === "decision" ? (live?.status === "open" ? "In Today" : "Decided")
    : it.kind === "research" ? (live?.status === "queued" ? "Queued for Claude" : live?.status === "in_progress" ? "Claude is on it" : live?.status === "failed" ? "Claude couldn't finish" : "Claude reported back")
    : "Drafted";
  const href = it.kind === "task" && it.ref ? `#/tasks?task=${it.ref.id}` : it.kind === "decision" ? "#/" : null;
  return <span className="row" style={{ gap: 6, flexShrink: 0 }}><Pill tone={live?.status === "failed" ? "warn" : "good"}>{text}</Pill>{href && <a className="small" href={href}>Open</a>}</span>;
}

function DraftResult({ it, onRedo, busy }: { it: any; onRedo: () => void; busy: boolean }) {
  const { toast } = useMe();
  return (
    <div className="stack" style={{ gap: 6, background: "var(--panel-2, #f6f4f8)", padding: 10, borderRadius: 8 }}>
      <div className="small" style={{ whiteSpace: "pre-wrap" }}>{it.result}</div>
      {it.result_notes && <span className="tiny muted">{it.result_notes}</span>}
      <div className="row" style={{ gap: 6 }}>
        <button className="btn sm" onClick={() => navigator.clipboard.writeText(it.result).then(() => toast("Copied"), () => toast("Couldn't copy", true))}>Copy</button>
        <button className="btn ghost sm" disabled={busy} onClick={onRedo}>{busy ? "Writing…" : "Write it again"}</button>
      </div>
    </div>
  );
}

// The same brain dump from any page: type, save, keep going. Claude sorts it in the background.
export function QuickCapture() {
  const { toast } = useMe();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") (e.preventDefault(), setOpen(true));
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  const send = () => {
    const t = text.trim();
    if (!t) return;
    setOpen(false);
    setText("");
    toast("Saved. Claude is sorting it into Brain dump.");
    api("/api/braindump", { method: "POST", body: { text: t } }).then(
      (r) => toast(r.error ? r.error : `Sorted into ${r.items.length} item${r.items.length === 1 ? "" : "s"}. Open Brain dump to say yes.`, !!r.error),
      (e) => toast(e.message, true),
    );
  };
  return (
    <>
      <button className="btn sm primary" onClick={() => setOpen(true)} title="Jot something down (⌘J)"><Plus size={15} strokeWidth={2.4} aria-hidden /> Jot it down</button>
      {open && (
        <Drawer title="Jot it down" sub="Anything on your mind. Claude sorts it; nothing happens until you say yes." onClose={() => setOpen(false)}>
          <div className="stack">
            <textarea className="input" autoFocus rows={8} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === "Enter" && send()} placeholder="e.g. Follow up with Zomee about December. Can Claude find what Tiny Trials said about their code?" aria-label="Jot it down" />
            <div className="row between">
              <span className="muted tiny">⌘ + Return to save · ⌘J opens this anywhere</span>
              <button className="btn primary" disabled={!text.trim()} onClick={send}>Save</button>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );
}
